/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import React, { useEffect, useState } from 'react';
import { NO_DATA_FALLBACK } from '../../../configuration';
import { ICoreContext } from '@msdyn365-commerce/core';
import { useModal } from '../../../components/modal';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import getPriceInfoAction, {
    GetPriceInfoInput,
    IGetPriceInfoDetailData,
    IGetPriceInfoDetailScales,
    IPriceInfoResponseData
} from '../../../actions/get-price-info.action';
import { IPriceInformationContext, IPriceInformationModalProps } from '../lxs-create-order.data';
import { IContext } from '@msdyn365-commerce/retail-proxy';
import { formatPriceIndication, formatScalePriceIndication } from '../../../utilities/format-price-indication';
import { removeTrailingZeros } from '../../../utilities';

const PriceInformationModal: React.FC<IPriceInformationModalProps> = ({ props, resources, productsData, orderData }) => {
    const [priceInformation, setPriceInformation] = useState<IPriceInfoResponseData>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const currentProduct = props.row.original;
    const { context } = useModal();
    const actionContext = (context as Partial<ICoreContext>) || {};
    const priceInfoContext: IPriceInformationContext = {
        context: context,
        actionContext: actionContext as ICoreContext
    };
    const CREATE_ORDER = 'lxs-create-order';

    const _getPriceInfo = (priceInfoInput: IGetPriceInfoDetailData, context: IContext): Promise<IPriceInfoResponseData> => {
        const actionInput = new GetPriceInfoInput(priceInfoInput);
        const actionContext = (context as Partial<ICoreContext>).actionContext;
        if (!actionContext) {
            return Promise.reject(new Error('Action context is undefined'));
        }
        return getPriceInfoAction(actionInput, actionContext);
    };

    /**
     * On load -> check for price information
     */
    useEffect(() => {
        if (productsData && orderData && currentProduct) {
            setIsLoading(true);
            const priceInfoInput = {
                SalesOrg: productsData?.SalesOrg,
                DistributionChannel: productsData?.DistributionChannel,
                Division: productsData?.Division,
                SoldToId: orderData.soldTo.Id,
                ShipToId: orderData.shipTo?.Id!,
                Incoterm: productsData.Incoterm1,
                EndCustomer: currentProduct.EndCustomerNumber,
                ProductNumber: currentProduct.ProductNumber,
                RequestedDate: currentProduct.RequestedDate,
                Language: priceInfoContext.actionContext.request.locale.split('-')[0]
            };

            const fetchPrices = async () => {
                const priceInformationResponse = await _getPriceInfo(priceInfoInput, context);
                setPriceInformation(priceInformationResponse);
            };
            void fetchPrices().then(() => setIsLoading(false));
        }
    }, [currentProduct]);

    return (
        <>
            {isLoading === false ? (
                <div className={`${CREATE_ORDER}__price-information`}>
                    <div className={`${CREATE_ORDER}__price-information-infobox`}>
                        <table className='table table-borderless table-sm mb-0'>
                            <tbody>
                                <tr>
                                    <td>
                                        <strong>{resources.createOrderProductsTableProductName}</strong>
                                    </td>
                                    <td>{currentProduct.ProductName ? currentProduct.ProductName : NO_DATA_FALLBACK}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>{resources.createOrderProductsTableProductNumber}</strong>
                                    </td>
                                    <td>{currentProduct.ProductNumber ? currentProduct.ProductNumber : NO_DATA_FALLBACK}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>{resources.additionalPriceInformationPopupBasePrice}</strong>
                                    </td>
                                    <td>{formatPriceIndication(currentProduct)}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>{resources.createOrderProductsTableIncoterm}</strong>
                                    </td>
                                    <td>{productsData?.Incoterm1}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {priceInformation?.Scales && priceInformation.Scales.length > 0 && (
                        <>
                            <h3 className='mb-3'>{resources.additionalPriceInformationPopupScalePrices}</h3>
                            <h5>{resources.additionalPriceInformationPopupScaleDiscounts}</h5>
                            <table className={`table ${CREATE_ORDER}__price-information-scale-price-table`}>
                                <thead>
                                    <tr>
                                        <th>{resources.additionalPriceInformationPopupScaleTerm}</th>
                                        <th>{resources.additionalPriceInformationPopupPricingTerm}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {priceInformation?.Scales.map((scale: IGetPriceInfoDetailScales, i: number) => (
                                        <tr key={`priceScaleInfo-${i}`}>
                                            <td>
                                                {removeTrailingZeros(scale.Bound)} {priceInformation.ScaleUnit}
                                            </td>
                                            <td>{formatScalePriceIndication(scale, priceInformation)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p className='mb-3'>
                                {priceInformation?.GroupScale === true
                                    ? resources.additionalPriceInformationPopupGroupScale
                                    : resources.additionalPriceInformationPopupWitoutGroupScale}
                            </p>
                        </>
                    )}
                    {priceInformation?.PaymentTerm && (
                        <>
                            <h5>{resources.additionalPriceInformationPopupPaymentTerms}</h5>
                            <p>{priceInformation?.PaymentTerm}</p>
                        </>
                    )}
                </div>
            ) : (
                <Spinner className={`lxs-create-order__loading`} size={SpinnerSize.large} />
            )}
        </>
    );
};

export default PriceInformationModal;
